import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import {
  addStateEmailDomain,
  editStateEmailDomain,
  rowEditEmailDomain,
  emailDomainFetching,
} from "../atoms/atoms";
import { IEmailDomain } from "../types";
import useEditEmailDomain from "../hooks/useEditEmailDomain";
import useAddEmailDomain from "../hooks/useAddEmailDomain";
import SubmitHookForm from "components/SubmitHookForm";
import EmailDomainDetails from "./EmailDomainDetails";

export const EmailDomainForm = (props: {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
}) => {
  // Drilled Props
  const { setRecent } = props;

  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateEmailDomain);
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateEmailDomain);
  const [editData, setEditData] = useRecoilState(rowEditEmailDomain);
  const setIsFetching = useSetRecoilState(emailDomainFetching);

  // Local States
  const [newData, setNewData] = useState<IEmailDomain>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hook Functions
  const updateData = useEditEmailDomain();
  const addData = useAddEmailDomain();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      emailDomainName: data.emailDomainName.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }

    setRecent("");
    setIsFetching(true);
    setIsSaving(false);
    setEditData(undefined);
    useFormMethods.reset();
    useFormMethods.clearErrors();
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Alarm Type" : "Edit Alarm Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <EmailDomainDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Alarm Type" : "Update Alarm Type"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default EmailDomainForm;
